import React from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";

// Import required modules
import { Pagination, Autoplay } from "swiper/modules";
import { Link } from "react-router-dom";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.3 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6, ease: "easeOut" } },
};

const Hero = () => {
  return (
    <div className="relative">
      <Swiper
        spaceBetween={20}
        pagination={{ clickable: true }}
        autoplay={{
          delay: 2000,
          disableOnInteraction: false,
        }}
        breakpoints={{
          0: { slidesPerView: 1 },
          640: { slidesPerView: 1 },
          768: { slidesPerView: 1 },
          1024: { slidesPerView: 1 },
        }}
        modules={[Pagination, Autoplay]}
        className="mySwiper !h-screen"
      >
        <SwiperSlide className="size-full relative">
          <img
            src="/assets/home/hero1.jpg"
            alt=""
            className="size-full object-cover"
          />
        </SwiperSlide>

        <SwiperSlide className="size-full relative">
          <img
            src="/assets/home/hero2.jpg"
            alt=""
            className="size-full object-cover"
          />
        </SwiperSlide>

        <SwiperSlide className="size-full relative">
          <img
            src="/assets/home/hero3.jpg"
            alt=""
            className="size-full object-cover"
          />
        </SwiperSlide>
      </Swiper>

      {/* Gradient overlay — deeper at bottom for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-themeColor/70 via-themeColor/60 to-themeColor/80 z-10" />

      <div className="absolute inset-0 z-20 flex items-center justify-center pt-[22%] md:pt-[15%] lg:pt-[10%] xl:pt-[8%]">
        <motion.div
          className="text-center text-white px-6 lg:px-4 max-w-5xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Eyebrow label */}
          <motion.p
            className="font-popinsMedium text-sm md:text-base uppercase tracking-widest text-white/80 mb-4"
            variants={fadeUp}
          >
            Commercial &amp; Small Bay Industrial Property Software
          </motion.p>

          {/* Main headline */}
          <motion.h1
            className="text-4xl md:text-5xl lg:text-7xl font-popinsBold mb-4 lg:tracking-[-4px] leading-tight"
            variants={fadeUp}
          >
            <span className="text-btnColor">The #1 Platform</span> for
            <br className="hidden md:block" /> Flex Space Operators
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            className="font-popinsRegular text-white/90 text-base md:text-lg lg:text-xl w-[95%] lg:w-[65%] mx-auto pt-3 leading-relaxed"
            variants={fadeUp}
          >
            Automate rent collection, track occupancy, manage NNN leases, and
            keep your commercial multi-tenant industrial portfolio running at peak
            performance — all from one dashboard.
          </motion.p>

          {/* Dual CTA row */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10"
            variants={fadeUp}
          >
            <Link to="/request-demo">
              <button className="animated-button">
                <svg viewBox="0 0 24 24" className="arr-2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
                </svg>
                <span className="text font-popinsSemiBold text-sm">Book a Free Demo</span>
                <span className="circle" />
                <svg viewBox="0 0 24 24" className="arr-1" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
                </svg>
              </button>
            </Link>

            <Link
              to="/package"
              className="font-popinsSemiBold text-sm text-white border border-white/60 rounded-full px-8 py-3 hover:bg-white hover:text-themeColor transition-all duration-300"
            >
              View Pricing
            </Link>
          </motion.div>

          {/* Quick trust line */}
          <motion.p
            className="font-popinsRegular text-white/60 text-xs mt-6 tracking-wide"
            variants={fadeIn}
          >
            No credit card required &nbsp;·&nbsp; Setup in minutes &nbsp;·&nbsp; Cancel anytime
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
};

export default Hero;
